import {
  LocationSearchType,
  LocationState
} from '@/components/buyerQualification/interfaces';
import { getOptionPropsFromAgent } from '@/components/qualification/projectForm/ProjectForm.utils';
import {
  Agent,
  PartialSquad,
  Squads,
  UserRole
} from '@/components/state/interfaces';
import { decode } from '@mapbox/polyline';
import { generateLatLng, Location, OptionProps } from '@proprioo/hokkaido';
import { colors } from '@proprioo/salatim';

import { PolygonAgent, PolygonIris, Zone } from './interfaces';

export const PARIS = {
  lat: 48.8677582,
  lng: 2.3387702
};

export const FRANCE = {
  lat: 46.227638,
  lng: 2.213749
};

export const generateTypeFromZoom = (zoom: number): LocationSearchType => {
  if (zoom > 11) {
    return LocationSearchType.IRIS;
  } else if (zoom > 10 && zoom < 12) {
    return LocationSearchType.MUNICIPALITY;
  }
  return LocationSearchType.COUNTY;
};

export const getPolygonsIris = (areas: LocationState[] = []): PolygonIris[] =>
  areas.map(({ id, geom, name, type }) => ({
    geom: geom.map(geo => geo.map(g => generateLatLng(decode(g)))).flat(1),
    id,
    name,
    type
  }));

export const getPrimaryPolygonsAgents = (agents: Zone[] = []): PolygonAgent[] =>
  agents.map(({ agentId, prospection }) => ({
    agentId,
    geom: prospection
      ?.map(geo => geo.map(g => generateLatLng(decode(g))))
      .flat(1)
  }));

export const getSecondaryPolygonsAgents = (
  agents: Zone[] = []
): PolygonAgent[] =>
  agents.map(({ agentId, lead }) => ({
    agentId,
    geom: lead?.map(geo => geo.map(g => generateLatLng(decode(g)))).flat(1)
  }));

export const getCenter = (geom: Location[][]): google.maps.LatLng => {
  const bounds = new google.maps.LatLngBounds();

  for (let i = 0; i < geom.length; i++) {
    for (let j = 0; j < geom[i].length; j++) {
      bounds.extend(geom[i][j]);
    }
  }

  return bounds.getCenter();
};

export const getPolygonColor = (
  agentId: number,
  activeAgents: Agent[],
  squads: Squads
): string => {
  const defaultColor = colors.dark.base;
  const activeAgent = activeAgents.find(({ id }) => id === agentId);
  const squadAgent = activeAgent?.squads[0]?.id;

  if (!squadAgent) {
    return defaultColor;
  }

  return squadAgent ? `#${squads[squadAgent].color}` : defaultColor;
};

export const getOptionPropsFromSquad = ({
  id,
  name
}: PartialSquad): OptionProps => ({ label: name, value: `${id}` });

export const getAgentGeometry = (
  polygonsAgent: PolygonAgent[],
  selectedAgentId: number
): Location[][] => {
  const [polygonCenterAgent] = polygonsAgent
    .filter(({ agentId, geom }) => agentId === selectedAgentId && geom)
    .map(({ geom }) => geom);

  return polygonCenterAgent;
};

export const getAgentsAndSquadsOptions = (
  agents: Agent[],
  squads: Squads
): OptionProps[] => {
  const squadsWithNumericNames = Object.values(squads)
    .filter(({ name }) => !isNaN(parseInt(name, 10)))
    .sort(
      (squadA, squadB) => parseInt(squadA.name, 10) - parseInt(squadB.name, 10)
    );

  return agents
    .filter(({ userRoles }) => !userRoles.includes(UserRole.SELLER_SUCCESS))
    .map(getOptionPropsFromAgent)
    .concat(
      Object.values(squads)
        .filter(({ name }) => isNaN(parseInt(name, 10)))
        .map(squad => getOptionPropsFromSquad(squad))
    )
    .sort((optionA, optionB) => (optionA.label > optionB.label ? 1 : -1))
    .concat(
      squadsWithNumericNames.map(squad => getOptionPropsFromSquad(squad))
    );
};
